import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpClient,
  HttpHeaders
} from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { throwError, BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Trade } from '@approot/models';
import { AlertsService } from '../alerts/alerts.service';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class TradesService {
  private _trades = new BehaviorSubject<Trade[]>([]);
  private dataStore: { trades: Trade[] } = { trades: [] };
  // this is the main observable that the trades grid watches
  readonly trades = this._trades.asObservable();
  savingInfo = '';

  constructor(
    private http: HttpClient,
    private alertsService: AlertsService,
    private authService: AuthService
  ) {}

  get savedTrades(): any {
    return Object.assign({}, this.dataStore).trades;
  }

  loadTrades() {
    this.authService
      .currentSession()
      .then(data => {
        this.http
          .get(
            'https://' +
              environment.api_domain +
              '.fxexotics.com/exotics/trades',
            this.getHeaders(data.getIdToken().getJwtToken())
          )
          .pipe(catchError(this.handleError.bind(this)))
          .subscribe((data: Trade[]) => {
            this.dataStore.trades = data;
            this._trades.next(Object.assign({}, this.dataStore).trades);
          });
      })
      .catch(error => {
        this.handleError(error);
      });
  }

  saveTrade(trade: Trade) {
    this.savingInfo = 'Saving';
    if (!trade.hasOwnProperty('timestamp')) {
      // add a timestamp in epoch time (milliseconds)
      trade['timestamp'] = new Date().getTime();
    }

    if (trade.hasOwnProperty('spot')) {
      // if there is a spot remove it
      delete trade['spot'];
    }

    if (trade.hasOwnProperty('secondSpot')) {
      // if there is a second spot remove it
      delete trade['secondSpot'];
    }

    Object.keys(trade).forEach(k => {
      if (k === 'schedule' && trade[k] && trade[k].length > 0) {
        let scheduleProcessed = JSON.parse(JSON.stringify(trade[k]));

        // we need to remove the missing attributes that exist within the schedule
        // this needs to occur to a copy of the data and not the schedule it self,
        // as changing the actual data, would result in the fields not being available at the time
        scheduleProcessed.map(x => {
          Object.keys(x).forEach(
            key => (x[key] = x[key] === '' ? null : x[key])
          );
        });
        trade[k] = scheduleProcessed;
        return;
      }
      !trade[k] && trade[k] !== undefined && delete trade[k];
    });

    this.authService
      .currentSession()
      .then(data => {
        this.http
          .put(
            'https://' +
              environment.api_domain +
              '.fxexotics.com/exotics/trades',
            trade,
            this.getHeaders(data.getIdToken().getJwtToken())
          )
          .pipe(catchError(this.handleError.bind(this)))
          .subscribe(
            data => {
              this.dataStore.trades.push(trade);
              this._trades.next(Object.assign({}, this.dataStore).trades);
              this.saveInterval('Saved');
            },
            error => {
              this.saveInterval('Failed');
            }
          );
      })
      .catch(error => {
        this.handleError(error);
      });
  }

  deleteTrade(userId: string, timestamp: number) {
    this.authService
      .currentSession()
      .then(data => {
        this.http
          .delete(
            'https://' +
              environment.api_domain +
              '.fxexotics.com/exotics/trades/object/' +
              userId +
              '/' +
              timestamp,
            this.getHeaders(data.getIdToken().getJwtToken())
          )
          .pipe(catchError(this.handleError.bind(this)))
          .subscribe(response => {
            this.dataStore.trades.forEach((t, i) => {
              if (t.timestamp === timestamp) {
                this.dataStore.trades.splice(i, 1);
              }
            });

            this._trades.next(Object.assign({}, this.dataStore).trades);
          });
      })
      .catch(error => {
        this.handleError(error);
      });
  }

  saveInterval(text: string) {
    this.savingInfo = text;
    setTimeout(() => {
      this.savingInfo = '';
    }, 6000);
  }

  private getHeaders(token) {
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', token);
    return { headers };
  }

  private handleError(error: HttpErrorResponse) {
    if (error) {
      if (error.error instanceof ErrorEvent) {
        // A client-side or network error occurred. Handle it accordingly.
        console.error('An error occurred:', error.error.message);
      } else {
        this.alertsService.addAlert({ message: error.error });
        // The backend returned an unsuccessful response code.
        // The response body may contain clues as to what went wrong,
        console.error(
          `Backend returned code ${error.status}, ` + `body was: ${error.error}`
        );
      }
    }

    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }
}
